import {
  ButtonGroup,
  ButtonV2,
  Form,
  IconDownload,
  IconExternalLink,
  IconHelp,
  InlineNotification,
  Loading,
  UploadDrawerLeadingFiles,
} from '@moneyforward/ap-frontend-components';

import {
  IMPORT_CSV_MAX_FILE_SIZE,
  IMPORT_CSV_MAX_FILE_SIZE_BYTE,
  IMPORT_CSV_UPLOAD_FILE_LIMIT_TOTAL,
} from '@/features/transaction_items/Detail/components/Drawer/ImportTransactionDetailOptionDrawer/constant';
import { useValidateFiles } from '@/features/transaction_items/Detail/components/Drawer/ImportTransactionDetailOptionDrawer/hooks/useImportCsvFileValidation';
import { TUploadFile } from '@/features/transaction_items/Detail/components/Drawer/ImportTransactionDetailOptionDrawer/type';
import { TransactionDetailOptionFormType } from '@/features/transaction_items/Detail/type';
import { useTranslation } from '@/i18n';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import classNames from 'classnames';
import { FC, memo, ReactNode, useCallback, useMemo, useState } from 'react';
import { useForm, UseFormReturn, useFormState } from 'react-hook-form';
import styles from './ImportTransactionDetailOptionDrawer.module.scss';

const cx = classNames.bind(styles);

export type ImportCsvForm = {
  file: TUploadFile[];
};

export type Props = {
  visible: boolean;
  onClose: () => void;
  methods: UseFormReturn<TransactionDetailOptionFormType>;
  notificationErrorVisible: boolean;
  notificationErrorContextHolder: ReactNode;
  clearNotificationErrorMessage: () => void;
};

export const ImportTransactionDetailOptionDrawer: FC<Props> = memo(
  ({
    visible,
    onClose,
    notificationErrorVisible,
    notificationErrorContextHolder,
    clearNotificationErrorMessage,
  }: Props) => {
    const { t } = useTranslation();

    const methods = useForm<ImportCsvForm>({
      defaultValues: {
        file: [],
      },
      mode: 'onChange',
    });
    const { control, reset, setValue } = methods;
    const { isSubmitting } = useFormState({ control });
    const [isFileValid, setIsFileValid] = useState(false);

    const csvFileSizeLimit = useMemo(
      () => ({
        bytes: IMPORT_CSV_MAX_FILE_SIZE_BYTE,
        label: IMPORT_CSV_MAX_FILE_SIZE,
      }),
      []
    );
    const validateFiles = useValidateFiles(csvFileSizeLimit);

    const onChange = useCallback(
      (file: TUploadFile | TUploadFile[]) => {
        const firstFile = Array.isArray(file) ? file[0] : file;
        if (!firstFile) return;
        const result = validateFiles(firstFile);
        firstFile.errorMessage = result.message;
        setIsFileValid(!result.isInvalid);
        queueMicrotask(() => setValue('file', [firstFile]));
      },
      [validateFiles, setValue]
    );

    const containerRef = useGlobalContainerRef();

    const onCloseDrawer = useCallback(async () => {
      reset({ file: [] });
      setIsFileValid(false);
      onClose();
      if (notificationErrorVisible) {
        clearNotificationErrorMessage();
      }
    }, [
      onClose,
      reset,
      notificationErrorVisible,
      clearNotificationErrorMessage,
    ]);

    const uploader = useMemo(
      () => ({
        append: false,
        description: (
          <>
            {t(
              'transaction_items.detail.options.drawer.import_file.uploader.description'
            )}
          </>
        ),
        accept: '.csv',
        name: 'file' as const,
        control: control,
        onChange,
      }),
      [t, control, onChange]
    );

    const onSubmit = useCallback((data: ImportCsvForm) => {
      // eslint-disable-next-line no-console
      console.log(data);
    }, []);

    return (
      <>
        <Loading
          open={isSubmitting}
          size='xlarge'
          color='white'
          isDialogMode
          getContainer={containerRef}
        />
        <Form<ImportCsvForm> formMethod={methods} onSubmit={onSubmit}>
          <div className={cx(styles['uploader-container'])}>
            <UploadDrawerLeadingFiles<ImportCsvForm>
              open={visible}
              onClose={onCloseDrawer}
              control={methods.control}
              name='file'
              arrayName='file'
              size='slim'
              uploader={uploader}
              uploadFileLimitTotal={IMPORT_CSV_UPLOAD_FILE_LIMIT_TOTAL}
              title={t(
                `transaction_items.detail.options.drawer.import_file.title`
              )}
              header={
                <>
                  <InlineNotification
                    message={notificationErrorContextHolder}
                    type='error'
                    visible={notificationErrorVisible}
                    onClick={clearNotificationErrorMessage}
                  />
                  <div className={cx(styles['header'])}>
                    <IconHelp size={14} />
                    {t(
                      'transaction_items.detail.options.drawer.import_file.description'
                    )}
                  </div>
                </>
              }
              footer={
                <ButtonGroup direction='horizontal'>
                  <ButtonV2
                    isTertiary
                    onClick={onCloseDrawer}
                    isDisabled={isSubmitting}
                  >
                    {t('cancel')}
                  </ButtonV2>
                  <ButtonV2
                    type='submit'
                    form='transaction_detail_option_form'
                    isDisabled={isSubmitting || !isFileValid}
                  >
                    {t('import')}
                  </ButtonV2>
                </ButtonGroup>
              }
              details={
                <div className={cx(styles['container'])}>
                  <>
                    <div className={cx(styles['title-container'])}>
                      <div className={cx(styles['title'])}>
                        {t(
                          'transaction_items.detail.options.drawer.import_file.detail.title'
                        )}
                      </div>
                      <div className={cx(styles['sub-title'])}>
                        {t(
                          'transaction_items.detail.options.drawer.import_file.detail.sub_title'
                        )}
                      </div>
                    </div>
                    <div className={cx(styles['detail-container'])}>
                      <div className={cx(styles['link-container'])}>
                        <a
                          href='/api/js/custom_transaction_items/options/import/sample_file?csv_encoding=shift_jis'
                          target='_blank'
                          className={cx(styles['link'])}
                        >
                          {t(
                            'transaction_items.detail.options.drawer.import_file.detail.shift_jis'
                          )}
                          <div className={cx(styles['icon'])}>
                            <IconDownload />
                          </div>
                        </a>
                        <a
                          href='/api/js/custom_transaction_items/options/import/sample_file?csv_encoding=utf-8'
                          target='_blank'
                          className={cx(styles['link'])}
                        >
                          {t(
                            'transaction_items.detail.options.drawer.import_file.detail.utf_8'
                          )}
                          <div className={cx(styles['icon'])}>
                            <IconDownload />
                          </div>
                        </a>
                      </div>
                    </div>
                  </>

                  <div className={cx(styles['support-link-container'])}>
                    <a
                      // TODO: replace it
                      href='https://biz.moneyforward.com/support/payable/guide/ap/ap01.html'
                      target='_blank'
                      className={cx(styles['link'])}
                    >
                      {t(
                        'transaction_items.detail.options.drawer.import_file.detail.support_link_text'
                      )}
                      <div className={cx(styles['icon'])}>
                        <IconExternalLink />
                      </div>
                    </a>
                  </div>
                </div>
              }
            />
          </div>
        </Form>
      </>
    );
  }
);
ImportTransactionDetailOptionDrawer.displayName =
  'ImportTransactionDetailOptionDrawer';
